const STORAGE_KEY = 'posthogSurveysSeen';

export type SurveyHandledStatus = 'answered' | 'dismissed';

interface SurveyHandledEntry {
  status: SurveyHandledStatus;
  at: string;
}

type UserSurveyMap = Record<string, SurveyHandledEntry>;
type SurveyHandledStore = Record<string, UserSurveyMap>;

function readStore(): SurveyHandledStore {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Migration: legacy flat shape was Record<surveyId, entry>. Detect by
    // checking whether any value lacks the nested structure and discard it
    // rather than leaking another user's dedup state into the current account.
    if (parsed && typeof parsed === 'object' && Object.values(parsed).some((v) => v && typeof v === 'object' && 'status' in (v as object))) {
      return {};
    }
    return parsed as SurveyHandledStore;
  } catch {
    return {};
  }
}

function writeStore(store: SurveyHandledStore) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* storage full or disabled — ignore */
  }
}

export function wasHandled(userId: string | undefined, surveyId: string): boolean {
  if (!userId) return false;
  return Boolean(readStore()[userId]?.[surveyId]);
}

export function markHandled(userId: string | undefined, surveyId: string, status: SurveyHandledStatus) {
  if (!userId) return;
  const store = readStore();
  const userMap = store[userId] ?? {};
  userMap[surveyId] = { status, at: new Date().toISOString() };
  store[userId] = userMap;
  writeStore(store);
}

export function clearAllHandled() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
